import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { api } from '../api/api';
import { openYoutubePlayerPage } from '../utils/youtubeUtils';

const { FiLoader, FiSettings } = FiIcons;

const shuffleDoors = (items) => {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }
  return shuffled;
};

const Snowfall = () => {
  const snowflakes = useMemo(() => Array.from({ length: 50 }), []);
  return (
    <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
      {snowflakes.map((_, index) => (
        <motion.div
          key={index}
          initial={{ top: -10, left: `${Math.random() * 100}%`, opacity: Math.random() * 0.5 + 0.2, scale: Math.random() * 0.5 + 0.5 }}
          animate={{ top: '110%', left: `${Math.random() * 100}%` }}
          transition={{ duration: Math.random() * 10 + 10, repeat: Infinity, ease: 'linear', delay: Math.random() * 20 }}
          className="absolute h-1 w-1 rounded-full bg-white blur-[1px]"
        />
      ))}
    </div>
  );
};

export default function Calendar() {
  const [doors, setDoors] = useState([]);
  const [settings, setSettings] = useState(null);
  const [appDate, setAppDate] = useState(new Date());
  const [openingId, setOpeningId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [loadedDoors, loadedSettings] = await Promise.all([api.getDoors(), api.getSettings()]);
        setDoors(shuffleDoors(loadedDoors));
        setSettings(loadedSettings);
        setAppDate(
          loadedSettings.useSimulatedDate && loadedSettings.simulatedDate
            ? new Date(loadedSettings.simulatedDate)
            : new Date()
        );
      } catch (error) {
        console.error('Fehler beim Laden:', error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const isUnlocked = (day) => {
    const month = appDate.getMonth();
    if (month === 0) return true;
    if (month === 11) return appDate.getDate() >= day;
    return false;
  };

  const getFullUrl = (assetPath) => {
    if (!assetPath) return null;
    if (/^(https?|blob:|data:)/.test(assetPath)) return assetPath;
    return (import.meta.env.DEV ? 'http://localhost:3002' : '') + assetPath;
  };

  const handleOpen = (door) => {
    if (!isUnlocked(door.day)) return;
    if (settings.soundPath) {
      const audio = new Audio(getFullUrl(settings.soundPath));
      audio.play().catch((error) => console.warn('Audio Error:', error));
    }
    const playerOpened = openYoutubePlayerPage(door.youtubeUrl, door.title || `Türchen ${door.day}`);
    if (!playerOpened) {
      console.warn('Der Player-Tab konnte nicht geöffnet werden.');
      return;
    }
    setOpeningId(door.id);
    window.setTimeout(() => setOpeningId(null), 850);
  };

  if (isLoading || !settings) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-black">
        <SafeIcon icon={FiLoader} className="animate-spin text-3xl text-white/20" />
      </div>
    );
  }

  const backgroundUrl = getFullUrl(settings.bgUploadPath) || getFullUrl(settings.bgImage);
  const logoUrl = getFullUrl(settings.logoPath);

  return (
    <div
      className="relative h-screen w-full overflow-y-auto overflow-x-hidden overscroll-contain bg-black bg-cover bg-center font-sans select-none"
      style={{ height: '100dvh', backgroundImage: `url(${backgroundUrl || ''})` }}
    >
      <div className="pointer-events-none fixed inset-0 bg-black/40" />
      <Snowfall />
      {logoUrl && (
        <div className="absolute left-5 top-5 z-50 md:left-10 md:top-10">
          <a
            href={settings.logoLink || '#'}
            onClick={(event) => !settings.logoLink && event.preventDefault()}
            className="block transition-all hover:scale-105 active:scale-95"
          >
            <div className="flex h-10 w-20 items-center justify-center overflow-hidden rounded-xl border border-white/20 bg-white/10 shadow-2xl backdrop-blur-3xl md:h-20 md:w-40 md:rounded-2xl">
              <img src={logoUrl} alt="Logo" className="h-full w-full object-contain p-2 md:p-3" />
            </div>
          </a>
        </div>
      )}
      <div className="relative z-10 min-h-full w-full px-[50px] py-16 sm:py-20 md:px-16 md:py-20">
        <div className="grid min-h-[720px] w-full grid-cols-4 grid-rows-6 gap-2 sm:gap-3 md:min-h-[calc(100dvh-160px)] md:grid-cols-6 md:grid-rows-4 md:gap-5">
          {doors.map((door, index) => {
            const unlocked = isUnlocked(door.day);
            const isOpening = openingId === door.id;
            return (
              <div key={door.id} className="flex min-h-0 min-w-0 items-center justify-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.01, type: 'spring', stiffness: 120 }}
                  className="relative aspect-square w-full max-w-16"
                  style={{ zIndex: isOpening ? 100 : 10 }}
                >
                  <motion.button
                    onClick={() => handleOpen(door)}
                    animate={isOpening ? { rotateY: -110, x: -10 } : { rotateY: 0, x: 0 }}
                    style={{ transformOrigin: 'left center' }}
                    className={`absolute inset-0 z-20 flex items-center justify-center rounded-xl border shadow-xl transition-all ${
                      unlocked
                        ? 'cursor-pointer border-white/30 bg-white/10 text-white backdrop-blur-2xl hover:scale-110 hover:border-white/80 active:scale-90'
                        : 'cursor-not-allowed border-white/5 bg-black/50 text-white/5'
                    }`}
                  >
                    <span className="text-[clamp(0.8rem,4vw,1.25rem)] font-black drop-shadow-md">
                      {door.day}
                    </span>
                  </motion.button>
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl border border-white/10 bg-black/40">
                    <div className="h-full w-full animate-pulse bg-gradient-to-br from-white/5 to-transparent" />
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
      <Link
        to="/login"
        className="fixed bottom-6 right-6 z-50 hidden h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/20 backdrop-blur-md hover:bg-white/10 hover:text-white/60 md:flex"
        title="Admin Login"
      >
        <SafeIcon icon={FiSettings} size={16} />
      </Link>
    </div>
  );
}